import { ActionButton, ActionButtonDefinition } from "components/actionButton/actionButton";
import { Action, ActionDefinition } from "components/actions/actions";
import { BackEventDetail } from "components/actions/backEvent";
import { ButtonBar } from "components/buttonBar/buttonBar";
import {
    AutoValueComponent,
    Component,
    DataHolderComponent,
    isAutoValueComponent,
    isValidationStatusComponent,
    NeedsUnhideComponent,
    ParentComponent,
    RestrictableComponent,
    ValidationStatusComponent,
    ValidatorComponent,
    ValueComponent,
    ValueComponentDefinition,
} from "components/components/Component";
import { listenDataUpdateRequest } from "components/components/componentUpdateRequestedUtil";
import { InputBaseComponent } from "components/components/InputBaseComponent";
import { ComponentActionEventDetail } from "components/events/componentActionHelper";
import { Renderer } from "components/Renderer";
import { buildConstraints } from "components/restrictions/constraints";
import { DependencyRunner } from "components/restrictions/dependencies/DependencyRunner";
import { LogicValidationUtils } from "components/restrictions/LogicValidationUtils";
import { FormRestrictions, isReadOnly, RestrictionAttribute } from "components/restrictions/Restrictions";
import $ from "jquery";
import { CancelConfirmation } from "../confirmationDialog/cancelConfirmation";
import { EventActionDefinition, EventActions } from "../events/eventActions";
import { Table, TableDefinition } from "../table/table";
import { DataChangedEventDetail, fireDataChangedCleared, fireDataChangedEvent } from "./dataChangedEventDetail";
import { expand as dotToObject } from "./dotToObject";
import template from "./form.hbs";
import "./form.scss";

export class Form extends InputBaseComponent<FormDefinition>
    implements ParentComponent, DataHolderComponent,
    RestrictableComponent, ValidatorComponent,
    NeedsUnhideComponent {
    public readonly componentType: string = "form";
    public children: Component[] = [];
    public originalData = {};
    public currentData = {};
    private firstData: any;
    private constraints: any;
    private restrictions: FormRestrictions;
    private buttons: ActionButton[];
    private changed: boolean = false;
    private valid: boolean = false;
    private eventActions: EventActions;
    private collapsed: any;
    private logicValidation: { [key: string]: string };
    private enabledButtons: { [key: string]: boolean };

    constructor(definition: FormDefinition) {
        super(definition);
        if (this.definition.actions != null) {
            this.definition.children = this.definition.children ?? [];
            this.definition.children.push({
                componentType: "buttonBar",
                id: "buttonBar-" + this.definition.id,
                children: this.definition.actions,
            });
        }
    }

    public render(): Element {
        let jqueryView = $(template(this.definition));
        this.view = jqueryView[0];

        jqueryView[0].addEventListener("dataChanged", this.dataChangedCallback);

        jqueryView[0].addEventListener("dataChangeRequest", (event) => {
            let suggestion = event.detail;
            const originalData = JSON.parse(JSON.stringify(this.currentData));
            this.changed = true;
            // This will also update the model of the form
            Renderer.instance().iterateToUpdateData(this, suggestion.data);
            this.updateFormStatus();
            fireDataChangedEvent(this, this.view, this.currentData, originalData, this.valid);
        });
        jqueryView[0].addEventListener("pageUnstackRequested", (event) => {
            if (this.changed && event.detail?.askForChangeConfirmation !== false) {
                new CancelConfirmation().confirm(this);
                event.stopPropagation();
                return false;
            } else {
                fireDataChangedCleared(this.definition.id, this.view);
            }
        });
        jqueryView[0].addEventListener(
            "clearDataCommitRequested",
            () => fireDataChangedCleared(this.definition.id, this.view));

        jqueryView[0].addEventListener("formUndoRequested", (event) => {
            event.stopPropagation();
            // Give parent components the option to cancel the form undo.
            const formUndoRequested = new CustomEvent("formUndoRequested", {
                bubbles: true,
                detail: {
                    undo: () => {
                        // do not dereference the object because the table filter needs to keep the reference.
                        this.setCurrentData(this.firstData);
                        this.originalData = JSON.parse(JSON.stringify(this.currentData));
                        Renderer.instance().iterateToSetData(this, this.currentData);
                        fireDataChangedCleared(this.definition.id, this.view);
                        let eventUndoed = new CustomEvent("formUndoed", {
                            bubbles: true,
                        });
                        this.view.dispatchEvent(eventUndoed);
                    },
                },
            });
            this.view.parentElement.dispatchEvent(formUndoRequested);
        });
        jqueryView[0].addEventListener("componentActionEvent", (event: CustomEvent<ComponentActionEventDetail>) => {
            event.stopPropagation();
            new Action(event.detail.action.action, this)
                .run(this.selectDataForAction(event.detail.action.action), this);
        });

        if (this.definition.collapsible) {
            $(jqueryView).find(".c-form__title").on("click", () => {
                this.toggle();
            });
        }

        this.eventActions = new EventActions(this.definition.eventActions, this);
        listenDataUpdateRequest(this);
        return this.view;
    }

    private clearCurrentData(): void {
        if (this.currentData == null) {
            this.currentData = {};
        }
        for (const prop of Object.keys(this.currentData)) {
            delete this.currentData[prop];
        }
    }

    private setCurrentData(data: object) {
        this.clearCurrentData();
        Object.assign(this.currentData, JSON.parse(JSON.stringify(data)));
    }

    private toggle() {
        if (this.collapsed) {
            this.expand();
        } else {
            this.collapse();
        }
    }

    public collapse() {
        this.collapsed = true;
        $(this.view).find(".c-form__title-collapsible-handler i")
            .addClass("fa-chevron-down")
            .removeClass("fa-chevron-up");
        $(this.view).find(".c-form__children, .c-form__button-bar-holder").hide();
    }

    private expand() {
        this.collapsed = false;
        $(this.view).find(".c-form__title-collapsible-handler i")
            .addClass("fa-chevron-up")
            .removeClass("fa-chevron-down");
        $(this.view).find(".c-form__children, .c-form__button-bar-holder").show();
    }

    private bindViewRemoval() {
        let observer = new MutationObserver((mutations) => {
            // check for removed target
            if (!document.getElementById("form-" + this.definition.id)) {
                observer.disconnect();
                this.eventActions.unbind();
            }
        });

        let config = {
            childList: true,
            subtree: true,
            characterData: false,
            attributes: false,
        };
        observer.observe(document.body, config);
    }

    private dataChangedCallback = (evt: CustomEvent<DataChangedEventDetail>) => {
        if (evt.target === this.view) {
            return;
        }
        evt.stopPropagation();
        if (evt.detail.isValid === true) {
            delete this.logicValidation[evt.detail.component.definition.property];
        } else if (evt.detail.isValid === false) {
            this.logicValidation[evt.detail.component.definition.property] = "";
        }
        if (!evt.detail.initializationChange) {
            this.changed = true;
        }
        let property = evt.detail.component.definition.property;

        if (property == null) {
            console.error("Setting undefined or null property!!!", evt);
            return;
        } else {
            this.currentData[property] = evt.detail.data;
            this.updateFormStatus();
        }
        // DO  not dispatch change event if change is for initialization
        if (!evt.detail.initializationChange) {
            fireDataChangedEvent(this, this.view, this.currentData, this.originalData, this.valid);
            this.eventActions.onChange(property, evt.detail.data, evt.detail.component);
        }
    }

    private updateFormStatus() {
        this.applyDependencies();
        this.validate();
        this.checkActionsStatus();
    }

    private validate() {
        let validate = require("validate.js");
        let validationResult = validate(this.currentData, this.constraints, { fullMessages: false });
        validationResult = { ...validationResult, ...this.logicValidation };

        Form.cleanNull(validationResult);
        validationResult = dotToObject(validationResult);

        Renderer.iterateComponentTree(this, isValidationStatusComponent, (c) => {
            if (validationResult[(c as ValueComponent).definition.property] == null) {
                (c as ValidationStatusComponent).setValidationStatus(true);
            } else {
                (c as ValidationStatusComponent).setValidationStatus(
                    false,
                    validationResult[(c as ValueComponent).definition.property]);
            }
        });
        this.valid = Object.keys(validationResult).length === 0;
    }

    private static cleanNull(obj) {
        for (let propName in obj) {
            if (obj[propName] === null || obj[propName] === undefined) {
                delete obj[propName];
            }
        }
    }

    private applyDependencies() {
        let dr = new DependencyRunner(this.currentData, this.restrictions);
        Renderer.iterateComponentTree(this, (c) => true, (c) => {
            dr.applyDependencies(c, this);
        });
        this.enabledButtons = this.enabledButtons ?? {};
        for (const button of this.buttons ?? []) {
            button.enable();
            dr.applyDependenciesToButton(button as ActionButton, this);
            this.enabledButtons[button.id] = button.isEnabled();
        }
        Renderer.iterateComponentTree(this,
            (c) => c instanceof Table,
            (c) => {
                (c as Table).applyDependenciesFromHolder(this.currentData);
            });
    }

    private checkActionsStatus() {
        for (let button of this.getActionButtons()) {
            if (button.definition.action != null
                && (
                    button.definition.action.needsChange
                    && !this.changed
                    || button.definition.action.needsValid
                    && !this.valid
                )) {
                button.disable();
            } else if (!this.isReadOnlyRestricted(button) && this.enabledButtons[button.id] !== false) {
                button.enable();
            }
        }
    }

    private getActionButtons(): ActionButton[] {
        let buttons = [];
        if (this.buttons == null) {
            return buttons;
        }
        return this.buttons.filter((b) => b.definition.action != null);
    }

    public appendChildView = (childView: Node): void => {
        $(childView).appendTo($(this.view).children(".c-form__children") as JQuery<HTMLElement>);

        if (this.definition.actions != null && Renderer.getComponentForView(childView)?.componentType === "buttonBar") {
            this.definition.children?.splice(this.definition.children.length - 1);
            let buttonBar = Renderer.getComponentForView(childView) as ButtonBar;
            let rendered = buttonBar.view;
            buttonBar.bindDataHolder(this);
            this.buttons = buttonBar.getButtons();

            $(this).on("fireParentRefreshNeeded", (event) => {
                $(rendered).trigger("parentRefreshNeeded");
            });
            $(this).on("parentRefreshNeeded", (event) => {
                Renderer.iterateComponentTree(
                    this,
                    (c) => isAutoValueComponent(c),
                    (c) => (c as AutoValueComponent).autoInitValue());
            });
            rendered.addEventListener("stackedPageReturned", (evt: CustomEvent<BackEventDetail>) => {
                if (evt.target !== rendered) {
                    fireDataChangedCleared(this.definition.id, this.view);
                    rendered.dispatchEvent(evt);
                }
            });
        }
    }

    public postRender = () => {
        if (this.buttons != null) {
            this.buttons.forEach(((button) => {
                let actionButton = (button as ActionButton);
                actionButton.postRender();
            }));
        }
        this.bindViewRemoval();
        if (this.definition.collapsed) {
            this.collapse();
        }
    }

    public updateData = (data: any): void => {
        this.currentData = this.currentData ?? {};
        // We do not override data in this method to be able to use it to perform partial updates.
        if (data == null) {
            data = this.originalData ?? {};
        } else {
            data = { ...this.currentData, ...data };
        }

        // We are going to modify the data for the form, but we need
        // to keep the original data for the renderer to set the values.
        // Make a deep copy.
        this.originalData = JSON.parse(JSON.stringify(data));
        this.setCurrentData(data);
        this.cleanUpTableData(this.currentData);

        if (this.firstData === undefined) {
            this.firstData = JSON.parse(JSON.stringify(this.originalData));
        }
        this.updateFormStatus();
    }

    public setCommittedStatus(): void {
        this.changed = false;
        this.originalData = JSON.parse(JSON.stringify(this.currentData));
        fireDataChangedCleared(this.definition.id, this.view);
    }

    /**
     * The model for a table received from the server is different than the model sent to the server.
     * The form needs to keep a format of the model with the changes, not with the original data.
     */
    private cleanUpTableData(data) {
        Renderer.iterateComponentTree(
            this,
            (c) => (c.componentType === "table" || c.componentType === "referenceTable"),
            (c) =>
                data[(c.definition as TableDefinition).property] = (c as Table).getValue(),
        );
    }

    public selectDataForAction(action: ActionDefinition): any {
        if (action.fieldParameterMappings == null || action.fieldParameterMappings.length === 0) {
            return this.currentData;
        } else {
            const data = {};
            for (const mapping of action.fieldParameterMappings) {
                data[mapping.as] = this.currentData[mapping.field];
            }
            return data;
        }
    }

    public setRequired(property: string, required: boolean): void {
        let restriction = this.restrictions[property];
        if (required) {
            if (restriction.attributes == null) {
                restriction.attributes = [];
            }
            if (!restriction.attributes.includes(RestrictionAttribute.required)) {
                restriction.attributes.push(RestrictionAttribute.required);
                this.initRestrictions(this.restrictions);
            }
        } else {
            if (restriction.attributes != null && restriction.attributes.includes(RestrictionAttribute.required)) {
                let index = restriction.attributes.indexOf(RestrictionAttribute.required);
                if (index > -1) {
                    restriction.attributes.splice(index, 1);
                    this.initRestrictions(this.restrictions);
                }
            }
        }
    }

    public initRestrictions = (restrictions: FormRestrictions): void => {
        this.logicValidation = { ...this.logicValidation ?? {}, ...LogicValidationUtils.getMessages(restrictions) };
        for (const key in this.restrictions) {
            if (this.restrictions.hasOwnProperty(key)) {
                const element = this.restrictions[key];
                element.attributes = restrictions[key]?.attributes ?? element.attributes;
                element.dependencies = restrictions[key]?.dependencies ?? element.dependencies;
                element.validations = restrictions[key]?.validations ?? element.validations;
            }
        }

        this.restrictions = { ...restrictions, ...this.restrictions ?? {} };
        this.constraints = buildConstraints(this.restrictions, this);
        this.applyRestrictionsToActions();
    }

    private applyRestrictionsToActions() {
        for (let button of this.getActionButtons()) {
            if (button.definition.action.actionName == null) {
                continue;
            }
            let restriction = this.restrictions[button.definition.action.actionName];
            button.applyRestrictions(restriction);
        }
    }

    private isReadOnlyRestricted(button: ActionButton) {
        if (this.restrictions == null) {
            return false;
        }
        let restriction = this.restrictions[button.definition.action.actionName];
        return isReadOnly(restriction);
    }

    public setValidationStatus(valid: boolean, message?: string) {
        // TODO This is wrong
    }

    public isValid(): boolean {
        return this.valid;
    }

    public isChanged(): boolean {
        return this.changed;
    }

    public unhide(): void {
        for (const b of this.getActionButtons()) {
            b.unhide();
        }
    }
}

export interface FormDefinition extends ValueComponentDefinition {
    titleKey?: string;
    collapsible?: boolean;
    collapsed?: boolean;
    preventLoss?: boolean;
    actions?: ActionButtonDefinition[];
    eventActions?: EventActionDefinition[];
}
